import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import withImmutablePropsToJS from "with-immutable-props-to-js"
import { OverlayTrigger, Tooltip } from "react-bootstrap"

// components
import { TopBarSearchify } from "QuorumDesign"

// selectors
import * as sharedSelectors from "shared/search/selectors"
import * as bulkActionsSelectors from "shared/search/searchify/bulkactions/selectors"
import * as searchifySelectors from "shared/search/searchify/selectors"

// action creators
import {
    clearSearchifySelections,
    toggleShowSearchifyCurrentSelection,
    searchifyAddCurrentFilter,
} from "shared/search/searchify/action-creators"
import { cancelSearchify } from "app/static/frontend/search-new/searchify/submitSearchify"
import { toggleSearchCountPrecision } from "app/static/frontend/search-new/action-creators"

// helpers
import { abbreviateNumber } from "shared/imports/sharedHelperFunctions"
import { useResponsiveSize } from "@/hooks"

const mapStateToProps = createStructuredSelector({
    countIsLoading: sharedSelectors.selectIsCountLoading,
    currentCount: sharedSelectors.selectCurrentCount,
    currentExactCount: sharedSelectors.selectCurrentExactCount,
    isExactCountInaccessible: sharedSelectors.selectIsExactCountInaccessible,
    initialCount: sharedSelectors.selectInitialCount,
    shouldShowExactCount: sharedSelectors.selectShouldShowExactCount,
    // searchify stuff
    bulkActionsCount: bulkActionsSelectors.selectBulkActionsCount,
    labelStack: searchifySelectors.selectSearchifyLabelStack,
    searchifyPathStackSize: searchifySelectors.selectSearchifyPathStackSize,
    searchifyOriginDetails: searchifySelectors.selectSearchifyOriginDetails,
    searchifyCountIsLoading: searchifySelectors.selectIsSearchifyCountLoading,
    searchifyCount: searchifySelectors.selectSearchifyCount,
    isFilterOnlySearchify: searchifySelectors.selectIsFilterOnlySearchify,
})

const mapDispatchToProps = {
    cancelSearchify,
    clearSearchifySelections,
    searchifyAddCurrentFilter,
    toggleSearchCountPrecision,
    toggleShowSearchifyCurrentSelection,
}

export const SearchCountBar = (props) => {
    const { isMobile } = useResponsiveSize()

    const getDataLabelSingular = (qdt) => {
        if (Userdata.isOnlyEUMode() && qdt === DjangIO.app.models.QuorumDataType.bill.value) {
            return "Item"
        }
        return props.labelSingular || DjangIO.app.models.QuorumDataType.by_value(qdt).singular
    }

    const getDataLabelPlural = (qdt) => {
        if (Userdata.isOnlyEUMode() && qdt === DjangIO.app.models.QuorumDataType.bill.value) {
            return "Items"
        }

        return props.labelPlural || DjangIO.app.models.QuorumDataType.by_value(qdt).plural
    }

    const getSearchifyRootBreadcrumb = () => {
        const headerName = props.searchifyOriginDetails.get("searchifyHeaderName")
        const searchType = props.searchifyOriginDetails.get("quorumDataType")

        return searchType ? `${headerName} (${getDataLabelPlural(searchType)}) / ` : `${headerName} / `
    }

    const getSearchifyTrailingBreadcrumbs = () =>
        // if there were more space, we'd show names of the search or something. But
        // since we're not, simple ellipsis function is best here.
        props.searchifyPathStackSize > 1 && `${"... / ".repeat(props.searchifyPathStackSize - 1)} `

    const getSearchifyCountString = () => {
        const total = props.currentCount.toLocaleString()
        const searchifyCount = props.searchifyCount.toLocaleString()

        if (props.searchifyCountIsLoading || props.countIsLoading) {
            return "(Counting...)"
        }

        return props.searchifyCount > 0 && !props.isFilterOnlySearchify
            ? `(${total} Total, ${searchifyCount} Selected)`
            : `(${total} Total)`
    }

    const renderSearchifyString = () => {
        return (
            <TopBarSearchify
                cancelSearchify={props.cancelSearchify}
                currentCount={props.currentCount.toLocaleString()}
                currentCountLoading={props.countIsLoading || props.searchifyCountIsLoading}
                dataTypeLabel={`Choose ${getDataLabelPlural(props.quorumDataType)}`}
                initialCount={props.initialCount.toLocaleString()}
                isMobile={isMobile}
                labelStack={props.labelStack}
                selectedCount={
                    props.searchifyCount > 0 && !props.isFilterOnlySearchify && props.searchifyCount.toLocaleString()
                }
            />
        )
    }

    const getPrecisionMaxThreshold = (qdt) => {
        switch (qdt) {
            case DjangIO.app.models.QuorumDataType.event.value:
            case DjangIO.app.models.QuorumDataType.person.value:
            case DjangIO.app.models.QuorumDataType.public_organization.value:
            case DjangIO.app.models.QuorumDataType.supporter.value:
                return 1e6
            case DjangIO.app.models.QuorumDataType.bill.value:
                return 55e3
            default:
                return 5e4
        }
    }
    const precisionMaxThreshold = getPrecisionMaxThreshold(props.quorumDataType)

    const renderCount = () => {
        const precisionMinThreshold = 2000
        const shouldBeAbleToRequestExactCount = props.currentCount <= precisionMaxThreshold

        // Only pass these props if precision toggling is appropriate.
        const precisionProps = {
            onClick: () =>
                props.toggleSearchCountPrecision(
                    props.currentExactCount,
                    props.isExactCountInaccessible,
                    props.shouldShowExactCount,
                    DjangIO.newResourceFromDataType(props.quorumDataType),
                    props.uniqueFlvId,
                ),
            style: { cursor: "pointer" },
        }

        return !props.countIsLoading ? (
            <h3 className="advanced-search-counter-found" {...(shouldBeAbleToRequestExactCount && precisionProps)}>
                <span className="advanced-search-counter-num">
                    {
                        // Do not abbreviate if showing exact count, or if below threshold.
                        props.shouldShowExactCount || props.currentCount < precisionMinThreshold
                            ? (props.currentExactCount || props.currentCount).toLocaleString()
                            : abbreviateNumber(props.currentCount, 1, precisionMinThreshold, true).toLocaleString()
                    }
                </span>
                <span className="advanced-search-counter-type" data-cy="advanced-search-counter-type">
                    {props.currentCount === 1
                        ? getDataLabelSingular(props.quorumDataType)
                        : getDataLabelPlural(props.quorumDataType)}
                    {props.isBulkActions &&
                        props.bulkActionsCount > 0 &&
                        ` (${props.bulkActionsCount.toLocaleString()} Selected)`}
                </span>
            </h3>
        ) : (
            <h3 className="advanced-search-counting">Counting...</h3>
        )
    }

    const renderCountWithPrecisionToggle = () => {
        const onEstimationHoverText =
            props.currentCount <= precisionMaxThreshold
                ? "Click to request the exact result count from the database."
                : "Unfortunately, this search has too many results to show an exact count."

        return (
            <OverlayTrigger
                key="search-count-overlay-trigger"
                placement="right"
                overlay={
                    <Tooltip key="search-count-tooltip">
                        {props.shouldShowExactCount
                            ? "Click to return back to the estimated value."
                            : onEstimationHoverText}
                    </Tooltip>
                }
            >
                {renderCount()}
            </OverlayTrigger>
        )
    }

    const renderCountString = () => (props.isSearchify ? renderSearchifyString() : renderCountWithPrecisionToggle())

    return renderCountString()
}

SearchCountBar.propTypes = {
    cancelSearchify: PropTypes.func,
    isBulkActions: PropTypes.bool,
    isSearchify: PropTypes.bool.isRequired,
    labelPlural: PropTypes.string,
    labelSingular: PropTypes.string,
    quorumDataType: PropTypes.number,
    // from store
    bulkActionsCount: PropTypes.number,
    countIsLoading: PropTypes.bool.isRequired,
    currentCount: PropTypes.number,
    currentExactCount: PropTypes.number,
    searchifyCount: PropTypes.number,
    searchifyCountIsLoading: PropTypes.bool.isRequired,
    searchifyPathStackSize: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(withImmutablePropsToJS(SearchCountBar))
